import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";

/** AI signals + day-trading feeds, with mute/hide symbol controls. */
export function useSignalFeeds(isAuthenticated, authHeaders, activeTab) {
  const [signals, setSignals] = useState(null);
  const [signalsLoading, setSignalsLoading] = useState(false);
  const [signalsError, setSignalsError] = useState(null);
  const [signalFilter, setSignalFilter] = useState("all");
  const [dayTrading, setDayTrading] = useState(null);
  const [dayTradingLoading, setDayTradingLoading] = useState(false);
  const [dayTradingError, setDayTradingError] = useState(null);
  const [dayTradingWatchlistOnly, setDayTradingWatchlistOnly] = useState(false);
  const [hiddenSymbols, setHiddenSymbols] = useState([]);
  const [muteSuggestions, setMuteSuggestions] = useState([]);

  const fetchSignals = useCallback(async () => {
    if (!isAuthenticated) {
      setSignalsError("signin");
      return;
    }
    setSignalsLoading(true);
    setSignalsError(null);
    try {
      const response = await axios.get(`${API}/predictions/signals`, {
        headers: authHeaders(),
      });
      setSignals(response.data);
    } catch (error) {
      if (error.response?.status === 402) {
        setSignalsError("payment");
      } else if (error.response?.status === 401) {
        setSignalsError("signin");
      } else {
        toast.error("Failed to fetch algorithm signals");
      }
    } finally {
      setSignalsLoading(false);
    }
  }, [isAuthenticated, authHeaders]);

  const fetchDayTrading = useCallback(async () => {
    if (!isAuthenticated) {
      setDayTradingError("signin");
      return;
    }
    setDayTradingLoading(true);
    setDayTradingError(null);
    try {
      const url = `${API}/predictions/day-trading${dayTradingWatchlistOnly ? "?watchlist_only=true" : ""}`;
      const response = await axios.get(url, { headers: authHeaders() });
      setDayTrading(response.data);
    } catch (error) {
      if (error.response?.status === 402) {
        setDayTradingError("payment");
      } else if (error.response?.status === 401) {
        setDayTradingError("signin");
      } else {
        toast.error("Failed to fetch day trading signals");
      }
    } finally {
      setDayTradingLoading(false);
    }
  }, [isAuthenticated, authHeaders, dayTradingWatchlistOnly]);

  const fetchHiddenSymbols = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const [hiddenRes, suggRes] = await Promise.all([
        axios.get(`${API}/hidden-symbols`, { headers: authHeaders() }),
        axios.get(`${API}/hidden-symbols/suggestions`, { headers: authHeaders() }),
      ]);
      setHiddenSymbols(hiddenRes.data.hidden || []);
      setMuteSuggestions(suggRes.data.suggestions || []);
    } catch (error) {
      console.error("Failed to fetch hidden symbols:", error);
    }
  }, [isAuthenticated, authHeaders]);

  const unhideSymbol = useCallback(async (symbol) => {
    try {
      await axios.delete(`${API}/hidden-symbols/${symbol}`, { headers: authHeaders() });
      toast.success(`${symbol} unmuted`);
      fetchHiddenSymbols();
      fetchDayTrading();
    } catch {
      toast.error("Failed to unmute symbol");
    }
  }, [authHeaders, fetchHiddenSymbols, fetchDayTrading]);

  const muteSuggestionPermanent = useCallback(async (symbol) => {
    // 30-day mute as "essentially permanent" — gives user time to reconsider
    try {
      await axios.post(`${API}/hidden-symbols`, { symbol, hours: 720 }, { headers: authHeaders() });
      toast.success(`${symbol} muted for 30 days`, {
        duration: 5000,
        action: { label: "Undo", onClick: () => unhideSymbol(symbol) },
      });
      fetchHiddenSymbols();
      fetchDayTrading();
    } catch {
      toast.error("Failed to mute symbol");
    }
  }, [authHeaders, fetchHiddenSymbols, fetchDayTrading, unhideSymbol]);

  const hideSymbol = useCallback(async (symbol) => {
    try {
      await axios.post(`${API}/hidden-symbols`, { symbol, hours: 24 }, { headers: authHeaders() });
      toast.success(`${symbol} muted for 24h`, {
        duration: 5000,
        action: {
          label: "Undo",
          onClick: () => unhideSymbol(symbol),
        },
      });
      fetchHiddenSymbols();
      fetchDayTrading();
    } catch {
      toast.error("Failed to mute symbol");
    }
  }, [authHeaders, fetchHiddenSymbols, fetchDayTrading, unhideSymbol]);

  useEffect(() => {
    if (activeTab === "signals") {
      fetchSignals();
    }
  }, [activeTab, fetchSignals]);

  useEffect(() => {
    if (activeTab === "day-trading") {
      fetchDayTrading();
      fetchHiddenSymbols();
    }
  }, [activeTab, fetchDayTrading, fetchHiddenSymbols]);

  return {
    signals, signalsLoading, signalsError, signalFilter, setSignalFilter,
    dayTrading, dayTradingLoading, dayTradingError,
    dayTradingWatchlistOnly, setDayTradingWatchlistOnly,
    hiddenSymbols, muteSuggestions, hideSymbol, unhideSymbol, muteSuggestionPermanent,
  };
}
